import { computed } from 'vue';

import type { DropdownMenuItem } from '@/shared/ui/dropdown';

import { useAuthStore } from '@/entities/auth';
import { useImpersonation } from '@/features/impersonation';
import { useThemeMenuItem } from '@/features/theme-switch';
import { resolvePlatformHomeUrl } from '@/shared/config';
import { externalLinkMenuItems } from '@/shared/ui/dropdown';
import { IconLogout, IconReceipt } from '@/shared/ui/icons';

/**
 * Дополнительные пункты `UserMenu`, общие для рейла и мобильной шторки: тема,
 * переход в HQ для суперпользователя, пульт эмулятора KKMServer и выход из режима impersonation.
 */
export function useUserMenuItems() {
  const authStore = useAuthStore();
  const { isStopping, stop } = useImpersonation();
  const themeItem = useThemeMenuItem();
  const hqItems = externalLinkMenuItems('platform-hq', 'HQ', () => resolvePlatformHomeUrl().toString());
  // Эмулятор ставится в `main.ts` до монтирования приложения и живёт до перезагрузки.
  const kkmFake = import.meta.env.DEV ? window.__kkmFake : undefined;
  const kkmFakeItems: DropdownMenuItem[] =
    kkmFake === undefined
      ? []
      : [
          {
            id: 'kkm-fake-panel',
            label: 'Эмулятор кассы',
            icon: IconReceipt,
            onClick: () => kkmFake.panel(),
          },
        ];

  return computed<DropdownMenuItem[]>(() => {
    // Смена организации — в плашке локации.
    if (!authStore.isImpersonating) {
      // Под impersonation в HQ возвращает «Выйти из режима»: @superuser там отказывает.
      return [themeItem.value, ...(authStore.user?.is_super_user ? hqItems : []), ...kkmFakeItems];
    }

    return [
      themeItem.value,
      ...kkmFakeItems,
      {
        id: 'impersonation-stop',
        label: isStopping.value ? 'Завершение...' : 'Выйти из режима',
        icon: IconLogout,
        loading: isStopping.value,
        disabled: isStopping.value,
        onClick: async () => {
          await stop();
        },
      },
    ];
  });
}
